import React, { useEffect, useState } from "react";
import { withFormik, Form, Field } from "formik";
import * as Yup from "yup";
import {
  FormGroup,
  Label,
  Button,
  Card,
  CardHeader,
  CardBody,
  Collapse,
  Col,
} from "reactstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "react-toastify";
import axios from "../../services/api/api";
import { API_BASE_URL } from "../../constants/api.constants";
import Select from "react-select";
import { TOAST_CONFIG } from "../../Utilities/toastConfig";
import { BASE_LOGIN_REDIRECT_URL_NO_LWC } from "../../constants/api.constants";
import ConfirmDialog from "../Modal/ConfirmDialog";
import { buildDownloadFileName } from "../../Utilities/fileHelper";
import { resizeImageForUpload } from "../../Utilities/imageResize";
import { formatServiceError } from "../../Utilities/serviceErrorHelper";

const CourseFileUploadField = ({
  labelId,
  downloadLabelId,
  courseName,
  inputName,
  currentUrl,
  removed,
  onSelectFile,
  showRemoveConfirm,
  setShowRemoveConfirm,
  confirmMessageId,
  onRemove,
}) => {
  const intl = useIntl();
  const downloadFileName = buildDownloadFileName(
    intl.formatMessage({ id: downloadLabelId }),
    courseName || intl.formatMessage({ id: "Course" }),
    currentUrl
  );

  return (
    <FormGroup row>
      <Label lg={3}>
        <FormattedMessage id={labelId} />
      </Label>
      <Col lg={9}>
        <input
          name={inputName}
          className="form-control"
          type="file"
          onChange={(event) => onSelectFile(event.currentTarget.files[0])}
        />

        {currentUrl && !removed ? (
          <div className="d-flex mt-2 align-items-center">
            <div className="me-1">
              <FormattedMessage id="CurrentFile" />
            </div>
            <a
              href={`${BASE_LOGIN_REDIRECT_URL_NO_LWC}/${currentUrl}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {downloadFileName}
            </a>
            <Button
              type="button"
              color="danger"
              outline
              size="sm"
              className="ms-3"
              onClick={() => setShowRemoveConfirm(true)}
            >
              <FormattedMessage id="Remove" />
            </Button>
          </div>
        ) : ""}

        <ConfirmDialog
          isOpen={showRemoveConfirm}
          toggle={() => setShowRemoveConfirm(false)}
          title={intl.formatMessage({ id: "Remove" })}
          message={intl.formatMessage({ id: confirmMessageId })}
          proceedKey="Remove"
          danger
          onProceed={onRemove}
        />
      </Col>
    </FormGroup>
  );
};

const AddCourseForm = ({
  values,
  errors,
  touched,
  isSubmitting,
  setFieldValue,
}) => {
  const intl = useIntl();
  const handleChange = (selected) => {
    values.selectedProgramLicenses = selected;
  };

  const [showRemoveGlossaryConfirm, setShowRemoveGlossaryConfirm] =
    useState(false);
  const [glossaryRemoved, setGlossaryRemoved] = useState(false);
  const [showRemoveTeacherGuideConfirm, setShowRemoveTeacherGuideConfirm] =
    useState(false);
  const [teacherGuideRemoved, setTeacherGuideRemoved] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);

  // imgFile starts as the saved image's URL and becomes a File once a new one is chosen.
  useEffect(() => {
    if (!(values.imgFile instanceof File)) {
      setSelectedImageUrl(null);
      return;
    }
    const url = URL.createObjectURL(values.imgFile);
    setSelectedImageUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [values.imgFile]);

  const imagePreviewUrl = selectedImageUrl ?? values.imgUrl;
  const imageCaptionId = selectedImageUrl
    ? "NewImageSavedOnSave"
    : "CurrentImage";

  const removeCourseFile = (
    endpoint,
    markRemoved,
    closeConfirm,
    removedMessageId
  ) => {
    axios
      .post(`${API_BASE_URL}/Course/${endpoint}?courseId=${values.courseId}`)
      .then(() => {
        markRemoved();
        closeConfirm();
        toast.success(<FormattedMessage id={removedMessageId} />, TOAST_CONFIG);
        if (values.refreshList) {
          values.refreshList();
        }
      })
      .catch((err) => {
        closeConfirm();
        toast.warn(
          formatServiceError(intl, err.response?.data, "CouldNotRemoveFile"),
          TOAST_CONFIG,
        );
      });
  };

  const removeGlossary = () =>
    removeCourseFile(
      "DeleteGlossary",
      () => {
        values.glossaryFile = null;
        setGlossaryRemoved(true);
      },
      () => setShowRemoveGlossaryConfirm(false),
      "GlossaryRemoved"
    );

  const removeTeacherGuide = () =>
    removeCourseFile(
      "DeleteTeacherGuide",
      () => {
        values.teacherGuideFile = null;
        setTeacherGuideRemoved(true);
      },
      () => setShowRemoveTeacherGuideConfirm(false),
      "TeacherGuideRemoved"
    );

  return (
    <div className="col-12 col-sm-12">
      <Form>
        <FormGroup row>
          <Label lg={3}><FormattedMessage id="Name" /></Label>
          <Col lg={9}>
            <Field
              className={`form-control ${
                errors.name && touched.name && "is-invalid"
              }`}
              type="input"
              name="name"
              value={values.name}
            />
            {errors.name && touched.name && (
              <div className="invalid-feedback">{errors.name}</div>
            )}
          </Col>
        </FormGroup>

        <FormGroup row>
          <Label lg={3}><FormattedMessage id="Description" /></Label>
          <Col lg={9}>
            <Field
              className={`form-control ${
                errors.description && touched.description && "is-invalid"
              }`}
              type="textarea"
              name="description"
            />
            {errors.description && touched.description && (
              <div className="invalid-feedback">{errors.description}</div>
            )}
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label lg={3}><FormattedMessage id="ImageIn16To9Format" /></Label>
          <Col lg={9}>
            <input
              id="imgFile"
              name="imgFile"
              type="file"
              accept=".jpg,.jpeg,.png"
              className={`form-control ${
                errors.imgFile && touched.imgFile && "is-invalid"
              }`}
              onChange={async (event) => {
                // The original is set at once so a submit during the resize
                // still carries a picture; the scaled copy replaces it.
                const selected = event.currentTarget.files[0];
                setFieldValue("imgFile", selected);
                setFieldValue("imgFile", await resizeImageForUpload(selected));
              }}
            />
            {errors.imgFile && touched.imgFile && (
              <div className="invalid-feedback">{errors.imgFile}</div>
            )}
            {imagePreviewUrl && (
              <div className="mt-2">
                <div className="small text-muted mb-1">
                  <FormattedMessage id={imageCaptionId} />
                </div>
                <a
                  href={imagePreviewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={imagePreviewUrl}
                    alt={intl.formatMessage({ id: imageCaptionId })}
                    className="img-thumbnail"
                    style={{
                      width: "100%",
                      maxWidth: 240,
                      aspectRatio: "16/9",
                      objectFit: "cover",
                    }}
                  />
                </a>
              </div>
            )}
          </Col>
        </FormGroup>
        <CourseFileUploadField
          labelId="TeacherGuide"
          downloadLabelId="Guide"
          courseName={values.name}
          inputName="teacherGuideFile"
          currentUrl={values.teacherGuideFileUrl}
          removed={teacherGuideRemoved}
          onSelectFile={(file) => {
            values.teacherGuideFile = file;
          }}
          showRemoveConfirm={showRemoveTeacherGuideConfirm}
          setShowRemoveConfirm={setShowRemoveTeacherGuideConfirm}
          confirmMessageId="RemoveTeacherGuideConfirm"
          onRemove={removeTeacherGuide}
        />
        <CourseFileUploadField
          labelId="Glossary"
          downloadLabelId="Glossary"
          courseName={values.name}
          inputName="glossaryFile"
          currentUrl={values.glossaryFileUrl}
          removed={glossaryRemoved}
          onSelectFile={(file) => {
            values.glossaryFile = file;
          }}
          showRemoveConfirm={showRemoveGlossaryConfirm}
          setShowRemoveConfirm={setShowRemoveGlossaryConfirm}
          confirmMessageId="RemoveGlossaryConfirm"
          onRemove={removeGlossary}
        />
        <FormGroup row>
          <Label lg={3}><FormattedMessage id="FortnoxArticleNumber" /></Label>
          <Col lg={9}>
            <Field
              className={`form-control ${
                errors.fortKnoxArticleNumber &&
                touched.fortKnoxArticleNumber &&
                "is-invalid"
              }`}
              type="input"
              name="fortKnoxArticleNumber"
            />
            {errors.fortKnoxArticleNumber && touched.fortKnoxArticleNumber && (
              <div className="invalid-feedback">
                {errors.fortKnoxArticleNumber}
              </div>
            )}
          </Col>
        </FormGroup>
        <Card>
          <CardHeader onClick={values.toggleExpand}>
            <FormattedMessage id="WebbasedLearnware" />{" "}
            <span
              className={
                values.isExpanded
                  ? "fa fa-caret-up pull-right"
                  : "fa fa-caret-down pull-right"
              }
            />
          </CardHeader>
          <Collapse isOpen={values.isExpanded}>
            <CardBody>
              <FormGroup row>
                <Label lg={3}><FormattedMessage id="Code" /></Label>
                <Col lg={9}>
                  <Field
                    className={`form-control ${
                      errors.code && touched.code && "is-invalid"
                    }`}
                    type="input"
                    name="code"
                  />
                  {errors.code && touched.code && (
                    <div className="invalid-feedback">{errors.code}</div>
                  )}
                </Col>
              </FormGroup>
              <FormGroup row>
                <Label lg={3}>LWGuid</Label>
                <Col lg={9}>
                  <Field
                    className={`form-control ${
                      errors.lwGuid && touched.lwGuid && "is-invalid"
                    }`}
                    type="input"
                    name="lwGuid"
                  />
                  {errors.lwGuid && touched.lwGuid && (
                    <div className="invalid-feedback">{errors.lwGuid}</div>
                  )}
                </Col>
              </FormGroup>
              <FormGroup row>
                <Label lg={3}><FormattedMessage id="LwStartModule" /></Label>
                <Col lg={9}>
                  <Field
                    className={`form-control ${
                      errors.lwStartModule &&
                      touched.lwStartModule &&
                      "is-invalid"
                    }`}
                    type="input"
                    name="lwStartModule"
                  />
                  {errors.lwStartModule && touched.lwStartModule && (
                    <div className="invalid-feedback">
                      {errors.lwStartModule}
                    </div>
                  )}
                </Col>
              </FormGroup>
              <FormGroup row>
                <Label lg={3}><FormattedMessage id="LwStartModuleGuid" /></Label>
                <Col lg={9}>
                  <Field
                    className={`form-control ${
                      errors.lwStartModuleGuid &&
                      touched.lwStartModuleGuid &&
                      "is-invalid"
                    }`}
                    type="input"
                    name="lwStartModuleGuid"
                  />
                  {errors.lwStartModuleGuid && touched.lwStartModuleGuid && (
                    <div className="invalid-feedback">
                      {errors.lwStartModuleGuid}
                    </div>
                  )}
                </Col>
              </FormGroup>
            </CardBody>
          </Collapse>
        </Card>
        <Card>
          <CardHeader>
            <FormattedMessage id="ProgramLicense" />
          </CardHeader>
          <CardBody>
            {values.courseId === 0 ? (
              <p>
                <FormattedMessage id="ProgramLicenseOnlyPossibleToAddInEditMode" />
              </p>
            ) : (
              <FormGroup>
                <Select
                  placeholder={intl.formatMessage({ id: "ChooseProgramLicenses" })}
                  isMulti
                  noOptionsMessage={() => intl.formatMessage({ id: "NoProgramLicenses" })}
                  onChange={(selected) => handleChange(selected)}
                  defaultValue={values.selectedProgramLicenses}
                  options={values.programLicenses.map((option) => ({
                    label: option.licenseName,
                    value: option.licenseName,
                    clearableValue: true,
                  }))}
                />
              </FormGroup>
            )}
          </CardBody>
        </Card>

        <Button
          type="submit"
          outline
          color="primary"
          className="float-end"
          disabled={isSubmitting}
        >
          <FormattedMessage id={isSubmitting ? "Wait" : "Save"} />
        </Button>
        <Button
          outline
          color="secondary"
          className="float-end me-2"
          onClick={() => values.dismissModal()}
        >
          <FormattedMessage id="Cancel" />
        </Button>
      </Form>
    </div>
  );
};

const FormikForm = withFormik({
  enableReinitialize: true,
  mapPropsToValues: (props, name) => {
    return {
      dismissModal: props.dismissModal,
      toggleExpand: props.toggleExpand,
      refreshList: props.refreshList,
      name: props.updateCourse ? props.updateCourse.name : "",
      description: props.updateCourse ? props.updateCourse.description : "",
      fortKnoxArticleNumber: props.updateCourse
        ? props.updateCourse.fortKnoxArticleNumber
        : "",
      imgFile: props.updateCourse ? props.updateCourse.imgUrl : "",
      imgUrl: props.updateCourse ? props.updateCourse.imgUrl : null,
      teacherGuideFile: null,
      teacherGuideFileUrl: props.updateCourse
        ? props.updateCourse.teacherGuideFileUrl
        : null,
      glossaryFile: null,
      glossaryFileUrl: props.updateCourse
        ? props.updateCourse.glossaryFileUrl
        : null,
      code: props.updateCourse ? props.updateCourse.code : "",
      lwGuid:
        props.updateCourse && props.updateCourse.lwGuid
          ? props.updateCourse.lwGuid
          : "",
      lwStartModule:
        props.updateCourse && props.updateCourse.lwStartModule
          ? props.updateCourse.lwStartModule
          : "",
      lwStartModuleGuid:
        props.updateCourse && props.updateCourse.lwStartModuleGuid
          ? props.updateCourse.lwStartModuleGuid
          : "",
      isExpanded: props.updateCourse
        ? props.updateCourse.lwGuid != null
          ? true
          : false
        : props.isExpanded,
      courseId: props.updateCourse ? props.updateCourse.id : 0,
      programLicenses: props.availableProgramLicenses,
      selectedProgramLicenses: props.updateCourse
        ? props.updateCourse.programLicenses.map((option) => ({
            label: option,
            value: option,
            clearableValue: true,
          }))
        : [],
      orgProgramLicenses: props.updateCourse
        ? props.updateCourse.programLicenses
        : [],
      handleToggleCourseAsLoading: props.handleToggleCourseAsLoading,
    };
  },

  validationSchema: Yup.object().shape({
    name: Yup.string().required(
      <FormattedMessage id="Views.Admin.Course.NameIsRequired" />
    ),
    fortKnoxArticleNumber: Yup.string().required(
      <FormattedMessage id="Views.Admin.Course.FortnoxArticleNumberIsRequired" />
    ),
  }),

  handleSubmit(values, { props, setSubmitting }) {
    const myData = new FormData();

    if (values.imgFile) {
      myData.append("ImageFile", values.imgFile);
    }

    if (values.teacherGuideFile) {
      myData.append("TeacherGuideFile", values.teacherGuideFile);
    }

    if (values.glossaryFile) {
      myData.append("GlossaryFile", values.glossaryFile);
    }

    if (values.courseId === 0) {
      axios
        .post(
          `${API_BASE_URL}/course?Name=${
            values.name
          }&Description=${encodeURIComponent(values.description)}&Code=${
            values.code === "null" || values.code === null ? "" : values.code
          }&fortKnoxArticleNumber=${values.fortKnoxArticleNumber}&lwGuid=${
            values.lwGuid
          }&lwStartModule=${values.lwStartModule}&lwStartModuleGuid=${
            values.lwStartModuleGuid
          }`,
          myData,
          { headers: { "Content-Type": "multipart/form-data;" } }
        )
        .then((response) => {
          toast.success(<FormattedMessage id="CourseAdded" />, TOAST_CONFIG);
          values.refreshList();
          values.dismissModal();
        })
        .catch((err) => {
          toast.warn(
            formatServiceError(props.intl, err.response?.data),
            TOAST_CONFIG,
          );
        })
        .finally(() => setSubmitting(false));
    } else {
      axios
        .put(
          `${API_BASE_URL}/course?id=${values.courseId}&Name=${
            values.name
          }&Description=${encodeURIComponent(values.description)}&Code=${
            values.code === "null" || values.code === null ? "" : values.code
          }&fortKnoxArticleNumber=${values.fortKnoxArticleNumber}&lwGuid=${
            values.lwGuid
          }&lwStartModule=${values.lwStartModule}&lwStartModuleGuid=${
            values.lwStartModuleGuid
          }`,
          myData,
          { headers: { "Content-Type": "multipart/form-data;" } }
        )
        .then((response) => {
          toast.success(<FormattedMessage id="CourseUpdated" />, TOAST_CONFIG);
          let toggledLoading = false;
          if (values.selectedProgramLicenses.length > 0) {
            values.selectedProgramLicenses.forEach((item) => {
              //Check that it was not already connected
              if (!values.orgProgramLicenses.includes(item.value)) {
                if (!toggledLoading) {
                  values.handleToggleCourseAsLoading(values.courseId);
                  toggledLoading = true;
                }
                axios
                  .post(
                    `${API_BASE_URL}/ProgramLicense/AddCoursesToProgramLicense?licenseName=${item.value}&courseIds=${values.courseId}`
                  )
                  .then(() => {
                    toast.success(
                      <FormattedMessage
                        id="CourseAddedToProgramLicenseName"
                        values={{ licenseName: item.value }}
                      />
                    );
                    if (toggledLoading) {
                      values.handleToggleCourseAsLoading(values.courseId);
                      toggledLoading = false;
                    }
                  })
                  .catch((err) => {
                    if (toggledLoading) {
                      values.handleToggleCourseAsLoading(values.courseId);
                      toggledLoading = false;
                    }
                    toast.warn(
                      err.response?.data ? (
                        formatServiceError(props.intl, err.response.data)
                      ) : (
                        <FormattedMessage
                          id="CouldNotAddCourseToProgramLicense"
                          values={{ licenseName: item.value }}
                        />
                      ),
                    );
                  });
              }
            });
          }
          values.orgProgramLicenses.forEach((currentCourseLicense) => {
            if (
              !values.selectedProgramLicenses.some(
                (item) => item.value === currentCourseLicense
              )
            ) {
              if (!toggledLoading) {
                toggledLoading = true;
                values.handleToggleCourseAsLoading(values.courseId);
              }
              axios
                .delete(
                  `${API_BASE_URL}/ProgramLicense/RemoveCoursesFromProgramLicense?licenseName=${currentCourseLicense}&courseId=${values.courseId}`
                )
                .then(() => {
                  toast.success(
                    <FormattedMessage id="CourseRemovedFromProgramLicense" />
                  );
                  if (toggledLoading) {
                    values.handleToggleCourseAsLoading(values.courseId);
                    toggledLoading = false;
                  }
                })
                .catch((err) => {
                  if (toggledLoading) {
                    values.handleToggleCourseAsLoading(values.courseId);
                    toggledLoading = false;
                  }
                  toast.warn(
                    formatServiceError(
                      props.intl,
                      err.response?.data,
                      "CouldNotDisconnectCourseFromProgramLicense",
                    ),
                  );
                });
            }
          });
          values.dismissModal();
          values.refreshList();
        })
        .catch((err) => {
          toast.warn(
            formatServiceError(props.intl, err.response?.data),
            TOAST_CONFIG,
          );
        })
        .finally(() => setSubmitting(false));
    }
  },
})(AddCourseForm);

const AddCourseFormWithIntl = (props) => {
  const intl = useIntl();

  return <FormikForm {...props} intl={intl} />;
};

export default AddCourseFormWithIntl;
